import {SC_GeoLocation} from "./SC_GeoLocation";

export class SC_Address {
    static readonly inputKeys = {
        country: 'country',
        county: 'county',
        state: 'state',
        city: 'city',
        zip: 'zip',
        streetLine1: 'streetLine1',
        streetLine2: 'streetLine2',
        timezoneID: 'timezoneID',
        region: 'region',
        geoLocation: 'geoLocation'
    };

    country: string | null;
    county: string | null;
    state: string | null;
    city: string | null;
    zip: string | null;
    street_1: string | null;
    street_2: string | null;
    timezone_id: string | null;
    region: string | null;
    geoLocation: SC_GeoLocation | null;

    constructor({
        country = null,
        county = null,
        state = null,
        city = null,
        zip = null,
        street_1 = null,
        street_2 = null,
        timezone_id = null,
        region = null,
        geoLocation = null
    } = {}) {
        this.country = country;
        this.county = county;
        this.state = state;
        this.city = city;
        this.zip = zip;
        this.street_1 = street_1;
        this.street_2 = street_2;
        this.timezone_id = timezone_id;
        this.region = region;
        this.geoLocation = geoLocation;
    }

    static fromAPI(jsonin: Record<string, any>): SC_Address {
        return new SC_Address({
            country: jsonin[this.inputKeys.country] ?? null,
            county: jsonin[this.inputKeys.county] ?? null,
            state: jsonin[this.inputKeys.state] ?? null,
            city: jsonin[this.inputKeys.city] ?? null,
            zip: jsonin[this.inputKeys.zip] ?? null,
            street_1: jsonin[this.inputKeys.streetLine1] ?? null,
            street_2: jsonin[this.inputKeys.streetLine2] ?? null,
            timezone_id: jsonin[this.inputKeys.timezoneID] ?? null,
            region: jsonin[this.inputKeys.region] ?? null,
            geoLocation: SC_GeoLocation.fromAPI(jsonin[this.inputKeys.geoLocation]),
        });
    }

    toAPI(): Record<string, unknown> {
        return {
            country: this.country,
            county: this.county,
            state: this.state,
            city: this.city,
            zip: this.zip,
            streetLine1: this.street_1,
            streetLine2: this.street_2,
            timezoneID: this.timezone_id,
            region: this.region,
            geoLocation: this.geoLocation?.toAPI() ?? null
        };
    }

    googleMapsLink(): string {
        return `https://google.com/maps/search/${encodeURIComponent([this.street_1, this.street_2, this.city, this.state, this.zip].filter(it => it != null).join(' '))}`
    }

    clone(): SC_Address {
        const cloned = new SC_Address();

        cloned.country = this.country;
        cloned.county = this.county;
        cloned.state = this.state;
        cloned.city = this.city;
        cloned.zip = this.zip;
        cloned.street_1 = this.street_1;
        cloned.street_2 = this.street_2;
        cloned.timezone_id = this.timezone_id;
        cloned.region = this.region;

        if (this.geoLocation) {
            cloned.geoLocation = this.geoLocation.clone();
        }

        return cloned;
    }

    /**
     * Merges another SC_Address object into this one, replacing null fields
     */
    merge(other: SC_Address): SC_Address {
        if (other.country !== null) this.country = other.country;
        if (other.county !== null) this.county = other.county;
        if (other.state !== null) this.state = other.state;
        if (other.city !== null) this.city = other.city;
        if (other.zip !== null) this.zip = other.zip;
        if (other.street_1 !== null) this.street_1 = other.street_1;
        if (other.street_2 !== null) this.street_2 = other.street_2;
        if (other.timezone_id !== null) this.timezone_id = other.timezone_id;
        if (other.region !== null) this.region = other.region;

        if (other.geoLocation !== null && this.geoLocation === null) {
            this.geoLocation = other.geoLocation.clone();
        } else if (other.geoLocation !== null && this.geoLocation !== null) {
            this.geoLocation.merge(other.geoLocation);
        }

        return this;
    }

    static basicEmpty(): SC_Address {
        return new SC_Address({
            geoLocation: SC_GeoLocation.basicEmpty()
        });
    }
}
